import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, LogOut, LayoutGrid, User, Settings } from 'lucide-react';
import { accountSignOut, accountAuthEventName, isAccountLoggedIn, getAccountEmail } from '../accountAuth';
import { Logo } from './Logo';
import { Button } from './ui';

interface SiteHeaderProps {
  className?: string;
}

export const SiteHeader = ({ className = '' }: SiteHeaderProps) => {
  const navigate = useNavigate();
  const [isAccountLoggedInState, setIsAccountLoggedInState] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply dark mode class on mount and when toggled
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  // Initialize dark mode from storage on first load
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') {
      document.documentElement.classList.add('dark');
      setIsDark(true);
    } else if (saved === 'light') {
      document.documentElement.classList.remove('dark');
      setIsDark(false);
    } else {
      setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
  }, []);

  // Check account login status
  useEffect(() => {
    const checkLoginStatus = () => {
      setIsAccountLoggedInState(isAccountLoggedIn());
    };

    checkLoginStatus();
    window.addEventListener('storage', checkLoginStatus);
    window.addEventListener(accountAuthEventName, checkLoginStatus);
    return () => {
      window.removeEventListener('storage', checkLoginStatus);
      window.removeEventListener(accountAuthEventName, checkLoginStatus);
    };
  }, []);

  const handleLogout = () => {
    accountSignOut();
    setIsAccountLoggedInState(false);
    navigate('/');
  };

  return (
    <header className={`sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50 dark:bg-slate-900/80 dark:border-slate-800 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 rounded-xl px-1 py-1 hover:opacity-80 transition-opacity"
            aria-label="Go to home"
          >
            <Logo size="lg" showText={false} showTagline={true} />
          </button>

          {/* Navigation */}
          <nav className="flex items-center gap-2 sm:gap-3">
            {/* Dark mode toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsDark(d => !d)}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="hidden sm:inline-flex"
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </Button>

            {/* Desktop nav */}
            <div className="hidden sm:flex items-center gap-2">
              <Button variant="primary" size="sm" onClick={() => navigate('/setup')}>
                Create
              </Button>
              {isAccountLoggedInState ? (
                <>
                  <Button variant="secondary" size="sm" onClick={() => navigate('/my-codes')}>
                    My Codes
                  </Button>
                  <button
                    onClick={() => navigate('/account/settings')}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors max-w-[140px]"
                    title="Account settings"
                  >
                    <Settings size={13} className="shrink-0" />
                    <span className="truncate">{getAccountEmail()}</span>
                  </button>
                  <Button variant="ghost" size="sm" onClick={handleLogout} aria-label="Log out">
                    <LogOut size={16} />
                  </Button>
                </>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => navigate('/account/login')}>
                  Log In
                </Button>
              )}
            </div>

            {/* Mobile nav */}
            <div className="sm:hidden flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setIsDark(d => !d)} aria-label={isDark ? 'Light mode' : 'Dark mode'}>
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/setup')} aria-label="Create code">
                Create
              </Button>
              {isAccountLoggedInState ? (
                <>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/my-codes')} aria-label="My Codes">
                    <LayoutGrid size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/account/settings')} aria-label="Settings">
                    <Settings size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleLogout} aria-label="Log out">
                    <LogOut size={16} />
                  </Button>
                </>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => navigate('/account/login')} aria-label="Log in">
                  <User size={16} />
                </Button>
              )}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};
